import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DayItineraryCard = ({ day, dayNumber, isExpanded, onToggle }) => {
  return (
    <div className="bg-card border border-border rounded-organic shadow-organic">
      {/* Day Header */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-organic rounded-t-organic"
      >
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <span className="font-heading font-heading-bold text-sm text-primary-foreground">{dayNumber}</span>
          </div>
          <div className="text-left">
            <h3 className="font-heading font-heading-bold text-lg text-card-foreground">{day?.title}</h3>
            <p className="font-caption text-sm text-muted-foreground">{day?.date}</p>
          </div>
        </div>
        <Icon 
          name={isExpanded ? "ChevronUp" : "ChevronDown"} 
          size={20} 
          className="text-muted-foreground transition-transform"
        />
      </button>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border">
          {/* **ENHANCED**: Park Image with Better Error Handling and Location Context */}
          <div className="mb-4 mt-4">
            <div className="w-full h-48 rounded-organic overflow-hidden relative">
              <Image 
                src={day?.parkImage || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=1200&q=80'} 
                alt={day?.parkImageAlt || `Beautiful landscape view of ${day?.title} state park with natural scenery and outdoor recreation opportunities`}
                className="w-full h-full object-cover"
              />
              {/* **NEW**: Park Website Link Overlay */}
              {day?.parkWebsite && (
                <div className="absolute top-3 right-3">
                  <a
                    href={day?.parkWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 bg-black/50 text-white px-2 py-1 rounded-organic-sm text-xs hover:bg-black/70 transition-colors"
                  >
                    <Icon name="ExternalLink" size={12} />
                    <span>Park Info</span>
                  </a>
                </div>
              )}
            </div>
            {/* **NEW**: Park Information and Links */}
            <div className="mt-3 p-3 bg-muted/20 rounded-organic">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-body font-body-medium text-sm text-card-foreground">
                    {day?.title?.split(' at ')?.[1] || day?.title?.split(' - ')?.[1] || 'Featured State Park'}
                  </h4>
                  <p className="font-caption text-xs text-muted-foreground">
                    Location-specific activities and attractions
                  </p>
                </div>
                {day?.parkWebsite && (
                  <div className="flex flex-col space-y-1">
                    <a
                      href={day?.parkWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-primary hover:underline text-xs"
                    >
                      <Icon name="Globe" size={12} />
                      <span>Official Website</span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Activities Timeline */}
          <div className="space-y-4">
            <h4 className="font-body font-body-medium text-base text-card-foreground mb-3">Daily Schedule</h4>
            {day?.activities?.map((activity, index) => (
              <div key={index} className="flex space-x-3">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center">
                    <Icon name="Clock" size={14} color="var(--color-accent)" />
                  </div>
                  {index < day?.activities?.length - 1 && (
                    <div className="w-0.5 h-8 bg-border mt-2"></div>
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-body font-body-medium text-sm text-accent">{activity?.time}</span>
                    <span className="w-1 h-1 bg-muted-foreground rounded-full"></span>
                    <span className="font-caption text-xs text-muted-foreground">{activity?.duration}</span>
                  </div>
                  <h5 className="font-body font-body-medium text-sm text-card-foreground mb-1">{activity?.title}</h5>
                  <p className="font-caption text-xs text-muted-foreground mb-2">{activity?.description}</p>
                  
                  {/* Location */}
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="MapPin" size={12} color="var(--color-muted-foreground)" />
                    <span className="font-caption text-xs text-muted-foreground">{activity?.location}</span>
                  </div>

                  {/* Essential Items */}
                  {activity?.essentialItems && activity?.essentialItems?.length > 0 && (
                    <div className="mt-2">
                      <p className="font-caption text-xs text-card-foreground mb-1">Bring:</p>
                      <div className="flex flex-wrap gap-1">
                        {activity?.essentialItems?.map((item, itemIndex) => (
                          <span 
                            key={itemIndex}
                            className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-organic-sm font-caption"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* **ENHANCED**: Driving Directions with Park Website Link */}
          <div className="mt-6 p-4 bg-muted/30 rounded-organic">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon name="Navigation" size={16} color="var(--color-primary)" />
                <h5 className="font-body font-body-medium text-sm text-card-foreground">Driving Directions</h5>
              </div>
              {day?.parkWebsite && (
                <a
                  href={day?.parkWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1 text-primary hover:underline text-xs"
                >
                  <Icon name="Map" size={12} />
                  <span>Detailed Map</span>
                </a>
              )}
            </div>
            <p className="font-caption text-xs text-muted-foreground mb-2">{day?.drivingDirections}</p>
            <div className="flex items-center space-x-4 text-xs">
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                <span className="font-caption text-muted-foreground">{day?.drivingTime}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Route" size={12} color="var(--color-muted-foreground)" />
                <span className="font-caption text-muted-foreground">{day?.distance}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayItineraryCard;